import { useState } from "react";
import { motion } from "framer-motion";
import { HandHeart, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

export default function PrayerForm() {
  const [form, setForm] = useState({ name: "", email: "", request: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.request.trim()) {
      toast.error("Please share your prayer request.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/prayer-requests", form);
      setDone(true);
      setForm({ name: "", email: "", request: "" });
      toast.success("Your prayer request has been received. We are praying with you.");
    } catch {
      toast.error("Could not submit your request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="prayer" data-testid="prayer-section" className="relative py-20 sm:py-28 bg-sec-2 overflow-hidden">
      <div className="absolute -bottom-1/4 -left-10 w-[40vw] h-[40vw] rounded-full blur-[140px] pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(225,6,0,0.18), transparent 70%)" }} />
      <div className="relative max-w-3xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="text-center mb-9">
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.25em] uppercase text-chapel-red">
              <HandHeart className="w-4 h-4" /> We&apos;d Love to Pray With You
            </span>
            <h2 className="mt-3 font-heading font-extrabold text-white text-3xl sm:text-5xl tracking-tight">
              Prayer Request
            </h2>
            <p className="mt-4 text-zinc-300 leading-relaxed">
              Share your prayer request and our prayer team will stand with you in faith.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="prayer-success"
              className="glass rounded-3xl p-10 text-center border border-white/10"
            >
              <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
              <h3 className="mt-4 font-heading font-bold text-white text-2xl">Thank you!</h3>
              <p className="mt-2 text-zinc-300">
                Your prayer request has been received. Be encouraged — we are praying with you.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="prayer-submit-another"
                className="mt-6 inline-flex items-center gap-2 bg-chapel-red hover:bg-chapel-glow text-white font-semibold rounded-full px-6 py-3 transition-colors duration-300"
              >
                Submit another request
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="glass rounded-3xl p-7 sm:p-9 border border-white/10 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Name (optional)</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    data-testid="prayer-name"
                    placeholder="Your name"
                    className="w-full bg-white border border-white/10 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-chapel-red focus:ring-2 focus:ring-chapel-red/40 focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Email (optional)</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    data-testid="prayer-email"
                    placeholder="you@example.com"
                    className="w-full bg-white border border-white/10 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-chapel-red focus:ring-2 focus:ring-chapel-red/40 focus:outline-none transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Your prayer request</label>
                <textarea
                  value={form.request}
                  onChange={set("request")}
                  data-testid="prayer-request"
                  rows={5}
                  placeholder="How can we pray for you?"
                  className="w-full bg-white border border-white/10 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-chapel-red focus:ring-2 focus:ring-chapel-red/40 focus:outline-none transition-colors resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="prayer-submit"
                className="inline-flex items-center justify-center gap-2 bg-chapel-red hover:bg-chapel-glow text-white font-semibold rounded-full px-8 py-3.5 transition-colors duration-300 disabled:opacity-60 red-glow"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandHeart className="w-4 h-4" />}
                Send Prayer Request
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
